import {Component, EventEmitter, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ObjectBaseComponent} from "../../../../../core/shared/object/mvs-object-base/object-base.component";
import {NavigationItem} from "../../../../../core/shared/dto/navigation/navigation-item";
import {TE_BLUEPRINT_NODE_VIEW_CONFIG} from "./shared/te-blueprint-node-view-config";
import {
    ConfigNodeAddEvent,
    ConfigNodeEditEvent,
    ConfigNodeSelectEvent
} from "../../../../../core/shared/components/mvs-config-node-view/data/config-node-view.interfaces";
import {MvsObjectNavigationService} from "../../../../../core/shared/navigation/mvs-object-navigation.service";
import {MvsObjectNavigationEntry} from "../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {MvsObjectNavigationActionEnum} from "../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsMessageService} from "../../../../../core/shared/service/message/mvs-message.service";
import {ConfirmationService} from "primeng/api";
import {ObserverService} from "../../../../../core/shared/object/service/observer.service";
import {
    ConfigNodeTypeEnum
} from "../../../../../core/shared/components/mvs-config-node-view/data/config-node-type.enum";
import {
    ConfigViewModeEnum
} from "../../../../../core/shared/components/mvs-config-node-view/data/config-view-mode.enum";
import {MvsFormControlOverwrite} from "../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite";
import {MvsFormFieldAccessEnum} from "../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum";
import {DtoDetail} from "../../../../../core/shared/dto/dto.detail";
import {MvsUiObjectService} from "../../../../../core/shared/object/service/mvs-ui-object.service";
import {MvsCrudModeEnum} from "../../../../../core/shared/service/crud/mvs-crud-mode.enum";
import {ObjectChangeInformation} from "../../../../../core/shared/logic/object-change-information";
import {
    MvsConfigNodeViewComponent
} from "../../../../../core/shared/components/mvs-config-node-view/mvs-config-node-view.component";

@Component({
    selector: 'te-blueprint-object-component',
    templateUrl: './te-blueprint-object.component.html',
    standalone: false
})
export class TeBlueprintObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    readonly ConfigViewModeEnum = ConfigViewModeEnum;

    viewMode: ConfigViewModeEnum = ConfigViewModeEnum.NODE_VIEW;

    displayMode: 'HIERARCHICAL' | 'COLLAPSED' | 'NORMAL' | null = null;

    readonly blueprintNodeViewConfig = TE_BLUEPRINT_NODE_VIEW_CONFIG;

    @Output() onNavigationItems = new EventEmitter<NavigationItem[]>();

    @ViewChild(MvsConfigNodeViewComponent) nodeViewComponent: MvsConfigNodeViewComponent;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected navigationService: MvsObjectNavigationService,
        protected observerService: ObserverService,
        protected uiObjectService: MvsUiObjectService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    onObjectChanged() {
        const title = `${this.dto.name} - Konfiguration`;
        this.setPageTitle(title);
        this.updateNavigationItems();
    }

    ngOnChanges(changes: any): void {
        if (!this.initialized) {
            return;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    onViewModeChange(mode: ConfigViewModeEnum): void {
        this.viewMode = mode;
        this.updateNavigationItems();
    }

    private updateNavigationItems(): void {
        if (this.viewMode === ConfigViewModeEnum.NODE_VIEW) {
            this.removeNavigationItems();
        }
    }

    handleNavigationItems(navigationItems: NavigationItem[]): void {
        if (this.viewMode === ConfigViewModeEnum.STANDARD) {
            this.onNavigationItems.emit(navigationItems);
        } else {
            this.removeNavigationItems();
        }
    }

    handleBreadcrumbItems(breadcrumbItems: any): void {
        this.onBreadcrumbItems.emit(breadcrumbItems);
    }

    // ==========================================
    // MvsConfigNodeViewComponent Event Handlers
    // ==========================================

    handleNodeSelect(event: ConfigNodeSelectEvent): void {
        const { entityId, entityType, node } = event;

        if (!entityId || !entityType) return;

        const isRootEntity = entityId === this.objectIdentifier.objectId &&
            entityType === this.objectIdentifier.objectType;

        const entryType = isRootEntity ? 'Form' : 'object';
        const navigationTarget = isRootEntity ? 'full' : 'side';

        const entry = MvsObjectNavigationEntry.createNavigationEntry(
            entityType,
            entityId,
            entryType,
            node?.name || '',
            '',
            '',
            MvsObjectNavigationActionEnum.edit,
            navigationTarget
        );

        this.navigationService.navigateTo(entry, 'right');
    }

    handleAddChild(event: ConfigNodeAddEvent): void {
        if (event.entityType) {
            let navItem = this.blueprintNodeViewConfig.navigationItems.find(
                nav => nav.entityType === event.entityType
            );

            if (!navItem) {
                for (const parentEntityType in this.blueprintNodeViewConfig.terminalRelationships) {
                    const relationships = this.blueprintNodeViewConfig.terminalRelationships[parentEntityType];
                    const found = relationships.find(rel => rel.entityType === event.entityType);
                    if (found) {
                        navItem = found;
                        break;
                    }
                }
            }

            if (navItem && event.context[0]) {
                const fkField = `${navItem.foreignKey}DtoId`;
                const parentEntityId = event.context[0].entityId;

                const defaultDto: DtoDetail = new DtoDetail();
                defaultDto[fkField] = parentEntityId;

                const formControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField(fkField, MvsFormFieldAccessEnum.READ);

                const childEntityType = event.entityType;

                this.uiObjectService.openObjectWidgetViaDialog(
                    null,
                    event.entityType,
                    0,
                    `${event.entityType}.object.view.widget.create`,
                    'Object',
                    defaultDto,
                    (changeInfo: ObjectChangeInformation) => {
                        if (changeInfo && changeInfo.after) {
                            const savedObjectId = changeInfo.after['id'];
                            if (savedObjectId > 0) {
                                if (this.nodeViewComponent) {
                                    this.nodeViewComponent.addEntityToCache(childEntityType, changeInfo.after);
                                } else {
                                    const objectIdentifier = new ObjectIdentifier(childEntityType, savedObjectId);
                                    this.observerService.refreshData.next(objectIdentifier);
                                }
                            }
                        }
                    },
                    MvsCrudModeEnum.create,
                    formControlOverwrite
                );
            }
        }
    }

    handleNodeEdit(event: ConfigNodeEditEvent): void {
        const { entityId, entityType, node } = event;

        if (!entityId || !entityType) {
            return;
        }

        this.uiObjectService.openObjectWidgetViaDialog(
            null,
            entityType,
            entityId,
            `${entityType}.object.view.widget.edit`,
            'Object',
            null,
            (changeInfo: ObjectChangeInformation) => {
                if (changeInfo && changeInfo.after) {
                    const savedObjectId = changeInfo.after['id'];
                    if (savedObjectId > 0) {
                        if (this.nodeViewComponent) {
                            this.nodeViewComponent.addEntityToCache(entityType, changeInfo.after);
                        } else {
                            const objectIdentifier = new ObjectIdentifier(entityType, savedObjectId);
                            this.observerService.refreshData.next(objectIdentifier);
                        }
                    }
                }
            },
            MvsCrudModeEnum.update,
            null
        );
    }

    handleDisplayModeChange(displayMode: 'HIERARCHICAL' | 'COLLAPSED' | 'NORMAL' | null): void {
        this.displayMode = displayMode;
    }

    getDisplayModeLabel(): string {
        switch (this.displayMode) {
            case 'HIERARCHICAL':
                return 'Hierarchical View';
            case 'COLLAPSED':
                return 'Collapsible View';
            case 'NORMAL':
                return 'Terminal View';
            default:
                return '';
        }
    }

    getDisplayModeIcon(): string {
        switch (this.displayMode) {
            case 'HIERARCHICAL':
                return 'fa-sitemap';
            case 'COLLAPSED':
                return 'fa-layer-group';
            case 'NORMAL':
                return 'fa-cubes';
            default:
                return '';
        }
    }

    getDisplayModeColorClass(): string {
        switch (this.displayMode) {
            case 'HIERARCHICAL':
                return 'bg-purple-50 text-purple-700';
            case 'COLLAPSED':
                return 'bg-orange-50 text-orange-700';
            case 'NORMAL':
                return 'bg-cyan-50 text-cyan-700';
            default:
                return 'bg-gray-50 text-gray-700';
        }
    }

    handleNodeViewBusyChange(busy: boolean): void {
    }

    handleNodeViewDataLoaded(): void {
    }

    protected readonly ConfigNodeTypeEnum = ConfigNodeTypeEnum;
}
